import { api } from "@/api/client";

export interface BillApproval {
  token: string;
  status: "pending" | "approved" | "rejected" | "closed";
  netsuite_bill_id: number;
  tranid: string | null;
  trandate: string | null;
  duedate: string | null;
  amount: number | null;
  memo: string | null;
  vendor_name: string | null;
  variance_note: string | null;
  approver_name: string | null;
  submitter_name: string | null;
  decided_by_name: string | null;
  decided_at: string | null;
  comment: string | null;
  pending_since: string | null;
  // Set when the approver said it should go to someone else; AP changes the approver.
  reroute: { requested_at: string; by_name: string | null; to_name: string | null; comment: string | null } | null;
  // A decision being saved to NetSuite in the background, and why the last one failed.
  deciding: { choice: "approve" | "reject"; since: string } | null;
  decision_error: string | null;
  has_pdf: boolean;
}

export interface ApprovalLine {
  line: number;
  item: string | null;
  account: string | null;
  memo: string | null;
  quantity: number | null;
  rate: number | null;
  amount: number | null;
  project: string | null;
  class_name: string | null;
  department: string | null;
  location: string | null;
}

export interface ApprovalPage {
  approval: BillApproval;
  lines: ApprovalLine[] | null; // null when NetSuite couldn't be read
  others: Pick<BillApproval, "token" | "vendor_name" | "tranid" | "amount">[];
  live: boolean;
}

// 403 with this shape means "sign in as the approver first".
export interface SignInRequired {
  code: "sign_in_required";
  approver_name: string | null;
  error: string | null;
}

const path = (token: string) => `/bill_approvals/${encodeURIComponent(token)}`;

export async function fetchApproval(token: string): Promise<ApprovalPage> {
  const { data } = await api.get<ApprovalPage>(path(token));
  return data;
}

export async function signInForApproval(token: string, credential: string): Promise<ApprovalPage> {
  const { data } = await api.post<ApprovalPage>(`${path(token)}/sign_in`, { credential });
  return data;
}

export async function fetchApprovalStatus(token: string): Promise<BillApproval> {
  const { data } = await api.get<{ approval: BillApproval }>(`${path(token)}/status`);
  return data.approval;
}

export async function decideApproval(token: string, choice: "approve" | "reject", comment: string): Promise<ApprovalPage> {
  const { data } = await api.post<ApprovalPage>(`${path(token)}/decision`, { choice, comment });
  return data;
}

// The invoice PDF as a blob (the app's copy, else the one attached in NetSuite);
// null when the bill has none.
export async function fetchApprovalPdf(token: string): Promise<Blob | null> {
  try {
    const { data } = await api.get<Blob>(`${path(token)}/pdf`, { responseType: "blob" });
    return data;
  } catch (e) {
    if ((e as { response?: { status?: number } })?.response?.status === 404) return null;
    throw e;
  }
}

export function signInRequired(error: unknown): SignInRequired | null {
  const resp = (error as { response?: { status?: number; data?: SignInRequired } })?.response;
  return resp?.status === 403 && resp.data?.code === "sign_in_required" ? resp.data : null;
}

export interface ApproverOption { netsuite_id: number; name: string }

export async function fetchApproverOptions(token: string, q: string): Promise<ApproverOption[]> {
  const { data } = await api.get<{ options: ApproverOption[] }>(`${path(token)}/approver_options`, { params: { q } });
  return data.options;
}

export async function requestReroute(token: string, toNetsuiteId: number, comment: string): Promise<ApprovalPage> {
  const { data } = await api.post<ApprovalPage>(`${path(token)}/reroute`, { to_netsuite_id: toNetsuiteId, comment });
  return data;
}

// One thing the approver did here, for the History and Rejections tabs.
export interface ApprovalEvent {
  token: string;
  vendor_name: string | null;
  tranid: string | null;
  amount: number | null;
  status: BillApproval["status"]; // where the bill stands now
  action: "approved" | "rejected" | "sent_back";
  at: string | null;
  comment: string | null;
  to_name: string | null;
}

export interface MyApprovals {
  name: string | null;
  email: string;
  live: boolean;
  waiting: BillApproval[];
  sent_back: BillApproval[];
  history: ApprovalEvent[];
}

export async function fetchMyApprovals(): Promise<MyApprovals> {
  const { data } = await api.get<MyApprovals>("/my_approvals");
  return data;
}

export async function signInForMyApprovals(credential: string): Promise<MyApprovals> {
  const { data } = await api.post<MyApprovals>("/my_approvals/sign_in", { credential });
  return data;
}
